import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface PersonDetailContextType {
  isOpen: boolean;
  personId: string | null;
  readOnly: boolean;
  darkMode: boolean;
  openPersonDetail: (id: string, readOnly?: boolean, darkMode?: boolean) => void;
  closePersonDetail: () => void;
  // Callback for when "Visit Profile" is clicked - allows different handling in operational mode
  onVisitProfile: ((personId: string) => void) | null;
  setOnVisitProfile: (handler: ((personId: string) => void) | null) => void;
  // Callback for when unit is clicked - allows navigation to units view in operational mode
  onUnitClick: ((unitId: string, unitName: string, blockId?: string) => void) | null;
  setOnUnitClick: (handler: ((unitId: string, unitName: string, blockId?: string) => void) | null) => void;
  // Callback for when department is clicked - allows navigation to department in operational mode
  onDepartmentClick: ((departmentId: string, departmentName: string) => void) | null;
  setOnDepartmentClick: (handler: ((departmentId: string, departmentName: string) => void) | null) => void;
  // Callback for when person data is updated - allows parent views to refresh their data
  onPersonUpdate: ((personId: string) => void) | null;
  setOnPersonUpdate: (handler: ((personId: string) => void) | null) => void;
  // Visitor action handlers - for manual check-in/check-out/revoke
  visitorActionHandlers: {
    onManualCheckIn?: (visitor: any) => void;
    onManualCheckOut?: (visitor: any) => void;
    onRevokeAccess?: (visitor: any) => void;
  } | null;
  setVisitorActionHandlers: (handlers: {
    onManualCheckIn?: (visitor: any) => void;
    onManualCheckOut?: (visitor: any) => void;
    onRevokeAccess?: (visitor: any) => void;
  } | null) => void;
}

const PersonDetailContext = createContext<PersonDetailContextType | undefined>(undefined);

export const PersonDetailProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [personId, setPersonId] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [onVisitProfile, setOnVisitProfileState] = useState<((personId: string) => void) | null>(null);
  const [onUnitClick, setOnUnitClickState] = useState<((unitId: string, unitName: string, blockId?: string) => void) | null>(null);
  const [onDepartmentClick, setOnDepartmentClickState] = useState<((departmentId: string, departmentName: string) => void) | null>(null);
  const [onPersonUpdate, setOnPersonUpdateState] = useState<((personId: string) => void) | null>(null);
  const [visitorActionHandlers, setVisitorActionHandlersState] = useState<{
    onManualCheckIn?: (visitor: any) => void;
    onManualCheckOut?: (visitor: any) => void;
    onRevokeAccess?: (visitor: any) => void;
  } | null>(null);

  const openPersonDetail = (id: string, isReadOnly: boolean = false, isDarkMode: boolean = false) => {
    setPersonId(id);
    setReadOnly(isReadOnly);
    setDarkMode(isDarkMode);
    setIsOpen(true);
  };

  const closePersonDetail = () => {
    setIsOpen(false);
    // Delay clearing personId to allow exit animation
    setTimeout(() => {
      setPersonId(null);
      setReadOnly(false);
      setDarkMode(false);
    }, 300);
  };

  const setOnVisitProfile = useCallback((handler: ((personId: string) => void) | null) => {
    setOnVisitProfileState(() => handler);
  }, []);

  const setOnUnitClick = useCallback((handler: ((unitId: string, unitName: string, blockId?: string) => void) | null) => {
    setOnUnitClickState(() => handler);
  }, []);

  const setOnDepartmentClick = useCallback((handler: ((departmentId: string, departmentName: string) => void) | null) => {
    setOnDepartmentClickState(() => handler);
  }, []);

  const setOnPersonUpdate = useCallback((handler: ((personId: string) => void) | null) => {
    setOnPersonUpdateState(() => handler);
  }, []);

  const setVisitorActionHandlers = useCallback((handlers: {
    onManualCheckIn?: (visitor: any) => void;
    onManualCheckOut?: (visitor: any) => void;
    onRevokeAccess?: (visitor: any) => void;
  } | null) => {
    setVisitorActionHandlersState(() => handlers);
  }, []);

  return (
    <PersonDetailContext.Provider
      value={{
        isOpen,
        personId,
        readOnly,
        darkMode,
        openPersonDetail,
        closePersonDetail,
        onVisitProfile,
        setOnVisitProfile,
        onUnitClick,
        setOnUnitClick,
        onDepartmentClick,
        setOnDepartmentClick,
        onPersonUpdate,
        setOnPersonUpdate,
        visitorActionHandlers,
        setVisitorActionHandlers,
      }}
    >
      {children}
    </PersonDetailContext.Provider>
  );
};

/**
 * Non-throwing variant for components that merely OFFER to open the person modal and have a
 * sensible fallback when it is not available (a link, a navigation). The throwing hook below stays
 * the default — a component that cannot work without the modal should still fail loudly.
 */
export const useOptionalPersonDetailContext = (): PersonDetailContextType | undefined =>
  useContext(PersonDetailContext);

export const usePersonDetailContext = () => {
  const context = useContext(PersonDetailContext);
  if (context === undefined) {
    throw new Error('usePersonDetailContext must be used within a PersonDetailProvider');
  }
  return context;
};
